/**
 * WizardStepReview — Step 4: Name, description, emoji picker, tags.
 * Full preview of all 3 pillars. "Save Persona" button.
 */

import React from 'react';
import { Input, TextArea, Badge } from '@components/ui';
import type { PersonaWizardData } from '@/types/persona.types';

const ICONS = ['🎭', '🎸', '🎤', '🥁', '🎺', '🎻', '🎵', '🔥', '💀', '🌙', '⚡', '🏴', '🐊', '🦅', '🌾', '🍺'];

interface WizardStepReviewProps {
  data: PersonaWizardData;
  onUpdate: (fields: Partial<PersonaWizardData>) => void;
}

export const WizardStepReview: React.FC<WizardStepReviewProps> = ({ data, onUpdate }) => {
  const hasIdentity = !!data.identity?.name;
  const hasVocal = !!data.vocalConfigJson;
  const hasProduction = data.productionArchitecture.length > 0;
  const hasInstrumentForge = !!data.instrumentConfigJson && data.instrumentConfigJson !== 'null';

  return (
    <div className="space-y-5">
      {/* Emoji picker */}
      <div className="space-y-2">
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Icon</label>
        <div className="flex flex-wrap gap-2">
          {ICONS.map(i => (
            <button
              key={i}
              onClick={() => onUpdate({ icon: i })}
              className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-all ${
                data.icon === i ? 'bg-accent/20 ring-2 ring-accent scale-110' : 'bg-slate-800 hover:bg-slate-700'
              }`}
            >
              {i}
            </button>
          ))}
        </div>
      </div>

      {/* Name */}
      <div className="space-y-2">
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Persona Name *</label>
        <Input
          value={data.name}
          onChange={e => onUpdate({ name: e.target.value })}
          placeholder="e.g., Southern Doom Setup, Highway Band Config..."
          className="!bg-slate-900 !border-slate-700"
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Description</label>
        <TextArea
          value={data.description}
          onChange={e => onUpdate({ description: e.target.value })}
          placeholder="What is this persona for?"
          rows={2}
          className="!bg-slate-900 !border-slate-700"
        />
      </div>

      {/* Tags */}
      <div className="space-y-2">
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Tags (comma-separated)</label>
        <Input
          value={data.tags}
          onChange={e => onUpdate({ tags: e.target.value })}
          placeholder="e.g., doom, heavy, experimental"
          className="!bg-slate-900 !border-slate-700"
        />
      </div>

      {/* Pillar preview */}
      <div className="space-y-2 pt-2">
        <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Persona Contents</p>
        <div className="grid grid-cols-3 gap-2">
          <div className={`p-3 rounded-lg border text-center ${
            hasIdentity ? 'border-green-500/30 bg-green-500/5' : 'border-slate-800 bg-slate-900/30'
          }`}>
            <p className="text-lg mb-1">{hasIdentity ? '✅' : '⬜'}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Identity</p>
            {data.identity?.name && <p className="text-[10px] text-slate-500 mt-1 truncate">{data.identity.name}</p>}
          </div>
          <div className={`p-3 rounded-lg border text-center ${
            hasVocal ? 'border-green-500/30 bg-green-500/5' : 'border-slate-800 bg-slate-900/30'
          }`}>
            <p className="text-lg mb-1">{hasVocal ? '✅' : '⬜'}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Vocals</p>
            {hasVocal && <p className="text-[10px] text-slate-500 mt-1">{data.vocalArchitecture.length} chars</p>}
          </div>
          <div className={`p-3 rounded-lg border text-center ${
            hasProduction ? 'border-green-500/30 bg-green-500/5' : 'border-slate-800 bg-slate-900/30'
          }`}>
            <p className="text-lg mb-1">{hasProduction ? '✅' : '⬜'}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Production</p>
            {hasProduction && (
              <p className="text-[10px] text-slate-500 mt-1">
                {data.productionArchitecture.length} chars{hasInstrumentForge ? ' + IF' : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
